import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, OctagonAlert, Search } from 'lucide-react';
import {
  stageMoveNeedsReason,
  type KanbanCard,
  type KanbanDueFilter,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import { AssignDialog, type AssignDialogResult } from '../components/assignments/assign-dialog.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { KanbanBoardView } from '../components/kanban/kanban-board.js';
import { ReasonDialog } from '../components/kanban/reason-dialog.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Input, ListSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useSession } from '../lib/session/session-context.js';
import {
  useBulkAssign,
  useCreateAssignment,
  useOpenProblems,
} from '../lib/assignments/use-assignments.js';
import {
  useBulkAdvance,
  useKanbanBoard,
  useMoveItemStage,
  useStageConfiguration,
} from '../lib/workflow/use-kanban.js';

/**
 * The board the shop runs its day from.
 *
 * Three behaviours here are the ones the shop would notice losing.
 *
 * A move is OPTIMISTIC: the card lands in the new column immediately, and if the server refuses
 * it goes visibly back where it came from with the server's own reason in a toast. The rollback
 * lives in `useMoveItemStage`; what this screen owns is telling the shop why.
 *
 * A move made with no connection is QUEUED rather than lost, and the card stays where it was
 * dropped — the write is late, not gone, and reverting the card would say otherwise.
 *
 * A backward move always asks for a reason before it is sent, so nothing goes back to stitching
 * without an answer to "why".
 */

const DUE_FILTERS: readonly KanbanDueFilter[] = ['all', 'overdue', 'today', 'week'];

export function BoardPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const session = useSession();

  const [due, setDue] = useState<KanbanDueFilter>('all');
  const [query, setQuery] = useState('');
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [garmentTypeKey, setGarmentTypeKey] = useState<string>('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [sendingBack, setSendingBack] = useState<{
    card: KanbanCard;
    toStage: WorkflowStage;
  } | null>(null);
  /** Either one card, or the whole selection at the start of a day. */
  const [assigning, setAssigning] = useState<{ cards: KanbanCard[] } | null>(null);

  const params = useMemo(
    () => ({
      due,
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(priorityOnly ? { priorityOnly: true } : {}),
      ...(garmentTypeKey ? { garmentTypeKey } : {}),
    }),
    [due, garmentTypeKey, priorityOnly, query],
  );

  const board = useKanbanBoard(params);
  const configuration = useStageConfiguration();
  const move = useMoveItemStage();
  const bulk = useBulkAdvance();

  // A tailor cannot send work back — the matrix says so, and the board must not offer what the
  // API would refuse. Anything other than a tailor may.
  const canRevert = session?.role !== 'tailor';
  // Handing work out is `assignments.write`, which is owner and manager. A tailor never reaches
  // this screen at all (the API sends them to their own), but the control is still gated on the
  // same rule rather than on being here.
  const canAssign = session?.role !== 'tailor';

  const assign = useCreateAssignment();
  const bulkAssign = useBulkAssign();
  const problems = useOpenProblems({ enabled: canAssign });

  // Remembered across fetches, not rebuilt from each one: the board comes back already filtered, so
  // once a garment is picked only that garment is left, and a list built from it alone would shrink
  // to one entry and hide the very control needed to go back to "All garments".
  const seenGarmentTypes = useRef(new Map<string, string>());
  const garmentTypes = useMemo(() => {
    for (const column of board.data?.columns ?? []) {
      for (const card of column.cards) {
        seenGarmentTypes.current.set(card.garmentTypeKey, card.garmentTypeLabel);
      }
    }
    return [...seenGarmentTypes.current.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [board.data]);

  const labelOf = (stage: WorkflowStage): string =>
    configuration.data?.stages.find((one) => one.key === stage)?.label ?? t(`stage.${stage}`);

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const toggleSelected = (itemId: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  /** Forward moves go straight through; backward ones stop for a reason first. */
  const onMove = (card: KanbanCard, toStage: WorkflowStage): void => {
    if (stageMoveNeedsReason(card.stage, toStage)) {
      setSendingBack({ card, toStage });
      return;
    }
    void submitMove(card, toStage);
  };

  const submitMove = async (
    card: KanbanCard,
    toStage: WorkflowStage,
    extra: { reason?: string; rework?: boolean } = {},
  ): Promise<void> => {
    try {
      const result = await move.mutateAsync({
        card,
        toStage,
        ...(extra.reason ? { reason: extra.reason } : {}),
        ...(extra.rework ? { rework: true } : {}),
      });
      if (result.queued) {
        toast({ title: t('kanban.queued', { garment: card.garmentTypeLabel }) });
      }
      setSendingBack(null);
    } catch (error) {
      // The card has already rolled back to where it was; this says why it did.
      report(error);
      setSendingBack(null);
    }
  };

  const onBulkAdvance = async (): Promise<void> => {
    try {
      const result = await bulk.mutateAsync([...selected]);
      setSelected(new Set());
      toast({
        title: t('kanban.bulkAdvanced', { count: result.moved.length }),
        ...(result.skipped.length > 0
          ? { description: t('kanban.bulkSkipped', { count: result.skipped.length }) }
          : {}),
        variant: result.skipped.length > 0 ? 'default' : 'success',
      });
    } catch (error) {
      report(error);
    }
  };

  /**
   * Hand one card, or a whole selection, to somebody.
   *
   * One garment goes through the single endpoint so the response can carry its warnings; a
   * selection goes through the bulk one, which reports what it could not assign rather than
   * dropping it. Both say what happened, because an end-of-day action that half-worked in
   * silence is the one the shop finds out about from a customer.
   */
  const onAssignConfirmed = async ({
    reason: _reason,
    ...input
  }: AssignDialogResult): Promise<void> => {
    const cards = assigning?.cards ?? [];
    try {
      if (cards.length === 1) {
        const result = await assign.mutateAsync({
          itemId: cards[0]!.itemId,
          ...input,
        });
        toast({
          title: t('assignments.assigned', { name: result.assignment.assignedToName }),
          ...(result.warnings.length > 0
            ? { description: result.warnings.map((one) => one.message).join(' · ') }
            : {}),
          variant: result.warnings.length > 0 ? 'default' : 'success',
        });
      } else {
        // Whatever the dialog was told about time and rank applies to every card in the
        // selection: one decision at the start of a day, eight garments.
        const result = await bulkAssign.mutateAsync({
          itemIds: cards.map((card) => card.itemId),
          ...input,
        });
        setSelected(new Set());
        toast({
          title: t('assignments.bulkAssigned', { count: result.created.length }),
          ...(result.skipped.length > 0
            ? { description: t('assignments.bulkSkipped', { count: result.skipped.length }) }
            : {}),
          variant: result.skipped.length > 0 ? 'default' : 'success',
        });
      }
      setAssigning(null);
    } catch (error) {
      report(error);
      setAssigning(null);
    }
  };

  const totals = board.data?.totals;
  const openProblems = problems.data?.items ?? [];

  return (
    <>
      <PageHeader title={t('kanban.title')} description={t('kanban.description')} />

      {/*
        At `md` the board takes the rest of the window and each column scrolls its own cards, so
        a busy stage never stretches the page. Phones keep the page scroll. See `KanbanBoardView`.
      */}
      <div data-fills-viewport="" className="flex flex-col gap-4 md:min-h-0 md:flex-1">
        {/*
          A karigar who has stopped on a garment told the shop so; this is where the shop is
          told back. It sits above the board rather than only on the flagged card, because the
          card in question may be three columns off the side of a phone screen.
        */}
        {openProblems.length > 0 ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/60 bg-destructive/5 p-3 text-sm"
          >
            <OctagonAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="min-w-0">
              <p className="font-medium">
                {t('assignments.problemsBanner', { count: openProblems.length })}
              </p>
              <ul className="mt-1 text-muted-foreground">
                {openProblems.slice(0, 3).map((one) => (
                  <li key={one.id} className="truncate">
                    {t('assignments.problemLine', {
                      garment: one.garmentTypeLabel,
                      order: one.orderNumber,
                      name: one.assignedToName,
                      problem: t(`assignments.problemKind.${one.problem?.kind ?? 'other'}`),
                    })}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              aria-label={t('kanban.searchLabel')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('kanban.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          {garmentTypes.length > 1 || garmentTypeKey !== '' ? (
            <select
              aria-label={t('kanban.garmentFilter')}
              value={garmentTypeKey}
              onChange={(event) => setGarmentTypeKey(event.target.value)}
              className="h-11 rounded-md border border-border bg-background px-3 text-sm"
            >
              <option value="">{t('kanban.allGarments')}</option>
              {garmentTypes.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {/* Scrolls sideways on a phone rather than wrapping into rows of chips. */}
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {DUE_FILTERS.map((one) => {
            const active = due === one;
            const count =
              one === 'overdue' ? totals?.overdue : one === 'today' ? totals?.dueToday : undefined;
            return (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setDue(one)}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border hover:bg-accent',
                )}
              >
                {t(`kanban.due.${one}`)}
                {count !== undefined && count > 0 ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-xs tabular-nums',
                      active ? 'bg-primary-foreground/20' : 'bg-muted',
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}

          <button
            type="button"
            role="tab"
            aria-selected={priorityOnly}
            onClick={() => setPriorityOnly((current) => !current)}
            className={cn(
              'shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              priorityOnly
                ? 'border-transparent bg-primary text-primary-foreground'
                : 'border-border hover:bg-accent',
            )}
          >
            {t('orders.urgent')}
          </button>
        </div>

        {totals ? (
          <p
            className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            role="status"
          >
            {t('kanban.inProgress', { count: totals.active })}
            {totals.overdue > 0 ? (
              <Badge variant="destructive">
                {t('kanban.overdueCount', { count: totals.overdue })}
              </Badge>
            ) : null}
            {totals.dueToday > 0 ? (
              <Badge variant="warning">
                {t('kanban.dueTodayCount', { count: totals.dueToday })}
              </Badge>
            ) : null}
          </p>
        ) : null}

        {board.isPending ? (
          <ListSkeleton rows={4} />
        ) : board.isError ? (
          <LoadError onRetry={() => void board.refetch()} />
        ) : (board.data?.totals.active ?? 0) === 0 &&
          (board.data?.columns ?? []).every((column) => column.cards.length === 0) ? (
          <EmptyState
            icon={LayoutGrid}
            title={t('kanban.emptyTitle')}
            description={t('kanban.emptyDescription')}
          />
        ) : (
          <KanbanBoardView
            board={board.data!}
            configuration={configuration.data}
            selected={selected}
            onToggleSelected={toggleSelected}
            onMove={onMove}
            onRevert={(card, toStage) => setSendingBack({ card, toStage })}
            {...(canAssign
              ? { onAssign: (card: KanbanCard) => setAssigning({ cards: [card] }) }
              : {})}
            canRevert={canRevert}
            movingItemId={move.isPending ? (move.variables?.card.itemId ?? null) : null}
          />
        )}
      </div>

      {/* The end-of-day bar. Fixed above the bottom navigation so a phone can reach it. */}
      {selected.size > 0 ? (
        <div className="fixed inset-x-0 bottom-16 z-20 mx-auto flex w-[min(36rem,calc(100%-2rem))] items-center justify-between gap-3 rounded-lg border border-border bg-background p-3 shadow-lg sm:bottom-4">
          <span className="text-sm font-medium">
            {t('kanban.selectedCount', { count: selected.size })}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              {t('common.cancel')}
            </Button>
            {canAssign ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setAssigning({
                    cards: (board.data?.columns ?? [])
                      .flatMap((column) => column.cards)
                      .filter((card) => selected.has(card.itemId)),
                  })
                }
              >
                {t('assignments.assignSelected')}
              </Button>
            ) : null}
            <Button size="sm" disabled={bulk.isPending} onClick={() => void onBulkAdvance()}>
              {t('kanban.advanceSelected')}
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        A selection that is all in one column has an obvious stage to default to; a mixed one
        has none, and guessing would be worse than asking.
      */}
      {assigning ? (
        <AssignDialog
          subject={
            assigning.cards.length === 1
              ? `${assigning.cards[0]!.garmentTypeLabel} · ${assigning.cards[0]!.orderNumber}`
              : t('assignments.garmentCount', { count: assigning.cards.length })
          }
          {...(assigning.cards.every((card) => card.stage === assigning.cards[0]?.stage)
            ? { currentStage: assigning.cards[0]!.stage }
            : {})}
          stageLabel={labelOf}
          pending={assign.isPending || bulkAssign.isPending}
          onCancel={() => setAssigning(null)}
          onConfirm={(input) => void onAssignConfirmed(input)}
        />
      ) : null}

      {sendingBack ? (
        <ReasonDialog
          card={sendingBack.card}
          toStage={sendingBack.toStage}
          stageLabel={labelOf(sendingBack.toStage)}
          pending={move.isPending}
          onCancel={() => setSendingBack(null)}
          onConfirm={({ reason, rework }) =>
            void submitMove(sendingBack.card, sendingBack.toStage, { reason, rework })
          }
        />
      ) : null}
    </>
  );
}

export default BoardPage;
